<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter, RouterLink } from 'vue-router';
import { useBuildsStore } from '../stores/builds';
import { useAuthStore } from '../stores/auth';
import { useWebSocket } from '../composables/useWebSocket';
import { useHistoricalLogs } from '../composables/useHistoricalLogs';
import BuildStatusBadge from '../components/BuildStatusBadge.vue';
import DispatchBanner from '../components/DispatchBanner.vue';
import LogViewer from '../components/LogViewer.vue';
import { formatTime, formatDuration } from '../utils/date';

const props = defineProps<{
  id: string;
}>();

const route = useRoute();
const router = useRouter();
const buildsStore = useBuildsStore();
const authStore = useAuthStore();

const buildId = props.id || (route.params.id as string);

// WebSocket for live builds
const {
  connected: wsConnected,
  logs: wsLogs,
  status,
  error: wsError,
  streamingState: wsStreamingState,
  connect: wsConnect,
  disconnect: wsDisconnect,
  clearLogs: wsClearLogs,
} = useWebSocket(buildId);

// HTTP fetch for historical builds
const {
  logs: historicalLogs,
  loadingState: historicalLoadingState,
  error: historicalError,
  fetchLogs: fetchHistoricalLogs,
  clearLogs: clearHistoricalLogs,
} = useHistoricalLogs(buildId);

// Track which mode we're using
const useHistorical = ref(false);

// Unified interface for LogViewer
const logs = computed(() => useHistorical.value ? historicalLogs.value : wsLogs.value);
const connected = computed(() => useHistorical.value ? false : wsConnected.value);
const streamingState = computed(() => {
  if (useHistorical.value) {
    switch (historicalLoadingState.value) {
      case 'loading':
        return 'replaying_history';
      case 'loaded':
        return 'finished';
      case 'error':
        return 'disconnected';
      default:
        return 'disconnected';
    }
  }
  return wsStreamingState.value;
});
const logError = computed(() => useHistorical.value ? historicalError.value : wsError.value);

function connect() {
  if (useHistorical.value) {
    fetchHistoricalLogs();
  } else {
    wsConnect();
  }
}

function disconnect() {
  wsDisconnect();
}

function clearLogs() {
  if (useHistorical.value) {
    clearHistoricalLogs();
  } else {
    wsClearLogs();
  }
}

// Sidebar collapsed state for mobile
const sidebarExpanded = ref(true);

function toggleSidebar() {
  if (window.innerWidth <= 900) {
    sidebarExpanded.value = !sidebarExpanded.value;
  }
}

// Artifact download state
const downloadingLogs = ref(false);
const downloadingStageArtifact = ref<string | null>(null); // tracks "stageName/artifactName" being downloaded
const downloadError = ref<string | null>(null);

// Check if artifacts are available
const hasLogs = computed(() => buildsStore.currentBuild?.artifacts?.logs != null);
const stageArtifacts = computed(() => buildsStore.currentBuild?.artifacts?.stages || []);
const hasStageArtifacts = computed(() => stageArtifacts.value.length > 0);
const hasArtifacts = computed(() => hasLogs.value || hasStageArtifacts.value);

// Terminal states that indicate a finished build
const terminalStates = ['success', 'failed', 'cancelled'];

onMounted(async () => {
  if (window.innerWidth <= 900) {
    sidebarExpanded.value = false;
  }

  await buildsStore.fetchBuild(buildId);

  // Determine if we should use HTTP (historical) or WebSocket (live)
  const buildStatus = buildsStore.currentBuild?.status;
  const isFinished = buildStatus && terminalStates.includes(buildStatus);

  if (isFinished) {
    // Build is finished - use HTTP to fetch logs
    useHistorical.value = true;
    if (authStore.token) {
      fetchHistoricalLogs();
    }
  } else {
    // Build is running/pending - use WebSocket for live streaming
    useHistorical.value = false;
    if (authStore.token) {
      wsConnect();
    }
  }
});

// Update build status when WebSocket receives status change
watch(status, async (newStatus) => {
  if (newStatus) {
    buildsStore.applyBuildUpdate({ id: buildId, status: newStatus as any });
    // Refetch full build data when build reaches a terminal state
    if (['success', 'failed', 'cancelled'].includes(newStatus)) {
      await buildsStore.fetchBuild(buildId);
    }
  }
});

// Auto-connect when build status changes to running (for pending builds)
watch(
  () => buildsStore.currentBuild?.status,
  (newStatus) => {
    if (newStatus === 'running' && !connected.value && authStore.token) {
      connect();
    }
  }
);

/** Hard-filter labels: every key/value must match a worker's labels. */
const buildRequires = computed<Record<string, string>>(() => {
  return buildsStore.currentBuild?.labels.requires ?? {};
});

/** Soft-filter labels: ranking boost for matching workers, never gating. */
const buildPrefers = computed<Record<string, string>>(() => {
  return buildsStore.currentBuild?.labels.prefers ?? {};
});

const hasRequires = computed(() => Object.keys(buildRequires.value).length > 0);
const hasPrefers = computed(() => Object.keys(buildPrefers.value).length > 0);

async function handleCancel() {
  if (confirm('Are you sure you want to cancel this build?')) {
    await buildsStore.cancelBuild(buildId);
  }
}

function handleRepeat() {
  const build = buildsStore.currentBuild;
  if (!build || !build.repositoryId) return;
  const query: Record<string, string> = {
    repositoryId: build.repositoryId,
    branch: build.branch,
  };
  if (build.pipelineName) {
    query.pipeline = build.pipelineName;
  }
  // Derive refType from build labels
  if (build.labels.prefers?.['git.tag']) {
    query.refType = 'tag';
  } else if (build.labels.prefers?.['pull_request.id']) {
    query.refType = 'pr';
  }
  router.push({ path: '/', query });
}

async function downloadLogs() {
  downloadingLogs.value = true;
  downloadError.value = null;

  try {
    const response = await fetch(`/api/builds/${buildId}/logs/download`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authStore.token}`,
      },
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || `HTTP ${response.status}`);
    }

    const { downloadUrl } = await response.json();
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    } else {
      throw new Error('No download URL returned');
    }
  } catch (e) {
    downloadError.value = e instanceof Error ? e.message : 'Download failed';
  } finally {
    downloadingLogs.value = false;
  }
}

async function downloadStageArtifact(stageName: string, artifactName: string) {
  const key = `${stageName}/${artifactName}`;
  downloadingStageArtifact.value = key;
  downloadError.value = null;

  try {
    const endpoint = `/api/builds/${buildId}/artifacts/stages/${encodeURIComponent(stageName)}/${encodeURIComponent(artifactName)}`;
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authStore.token}`,
      },
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || `HTTP ${response.status}`);
    }

    const { downloadUrl } = await response.json();
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    } else {
      throw new Error('No download URL returned');
    }
  } catch (e) {
    downloadError.value = e instanceof Error ? e.message : 'Download failed';
  } finally {
    downloadingStageArtifact.value = null;
  }
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Check if error message indicates build was superseded
const isSuperseded = computed(() => {
  const msg = buildsStore.currentBuild?.errorMessage;
  return msg?.startsWith('Superseded by build ') ?? false;
});

// Extract superseding build ID from error message
const supersedingBuildId = computed(() => {
  const msg = buildsStore.currentBuild?.errorMessage;
  if (!msg) return null;
  const match = msg.match(/^Superseded by build ([a-f0-9-]+)$/);
  return match ? match[1] : null;
});

// Format trigger source for display
function formatTriggerSource(source?: string): string {
  switch (source) {
    case 'ui': return 'Manual (UI)';
    case 'api': return 'API';
    case 'cli': return 'CLI';
    case 'webhook': return 'GitLab Webhook';
    case 'schedule': return 'Scheduled';
    default: return source || 'Unknown';
  }
}
</script>

<template>
  <div class="build-detail">
    <div class="top-bar">
      <button @click="router.push('/')" class="back-btn">
        &larr; Back to Builds
      </button>
      <div v-if="buildsStore.currentBuild" class="build-actions">
        <button
          v-if="authStore.canWrite"
          @click="handleRepeat"
          class="btn-repeat"
        >
          Repeat
        </button>
        <button
          v-if="authStore.canWrite && ['pending', 'running'].includes(buildsStore.currentBuild.status)"
          @click="handleCancel"
          class="btn-cancel"
        >
          Cancel
        </button>
      </div>
    </div>

    <div v-if="buildsStore.loading" class="loading">
      Loading...
    </div>

    <div v-else-if="buildsStore.error" class="error">
      {{ buildsStore.error }}
    </div>

    <div v-else-if="buildsStore.currentBuild" class="build-layout">
      <!-- Sidebar with metadata -->
      <aside class="build-sidebar">
        <div class="build-header" @click="toggleSidebar">
          <h1>Build {{ buildsStore.currentBuild.id.substring(0, 8) }}</h1>
          <BuildStatusBadge :status="buildsStore.currentBuild.status" />
          <span class="sidebar-toggle" :class="{ expanded: sidebarExpanded }">&#x25BE;</span>
        </div>

        <div v-show="sidebarExpanded" class="sidebar-content">
        <div class="info-list">
          <div class="info-item">
            <span class="label">Repository</span>
            <RouterLink
              :to="{ name: 'repository-detail', params: { slug: buildsStore.currentBuild.organizationSlug, repoName: buildsStore.currentBuild.repositoryName } }"
              class="value link"
            >
              {{ buildsStore.currentBuild.repositoryName }}
            </RouterLink>
          </div>
          <div class="info-item">
            <span class="label">Branch</span>
            <RouterLink
              :to="{ name: 'repository-detail', params: { slug: buildsStore.currentBuild.organizationSlug, repoName: buildsStore.currentBuild.repositoryName }, query: { branch: buildsStore.currentBuild.branch } }"
              class="value link mono"
            >
              {{ buildsStore.currentBuild.branch }}
            </RouterLink>
          </div>
          <div class="info-item">
            <span class="label">Duration</span>
            <span class="value">
              {{ formatDuration(buildsStore.currentBuild.startedAt, buildsStore.currentBuild.finishedAt) }}
              <span v-if="buildsStore.currentBuild.startedAt" class="time-range">
                {{ formatTime(buildsStore.currentBuild.startedAt) }}
                <span v-if="buildsStore.currentBuild.finishedAt"> → {{ formatTime(buildsStore.currentBuild.finishedAt) }}</span>
              </span>
            </span>
          </div>
          <div class="info-item">
            <span class="label">Triggered</span>
            <span class="value">
              <span class="trigger-badge" :class="buildsStore.currentBuild.triggerSource">
                <svg v-if="buildsStore.currentBuild.triggerSource === 'schedule'" class="trigger-badge-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                {{ formatTriggerSource(buildsStore.currentBuild.triggerSource) }}
              </span>
            </span>
          </div>
          <div v-if="buildsStore.currentBuild.workerId" class="info-item">
            <span class="label">Worker</span>
            <span class="value">
              <span v-if="buildsStore.currentBuild.workerName">{{ buildsStore.currentBuild.workerName }}</span>
              <span v-else class="mono">{{ buildsStore.currentBuild.workerId.substring(0, 8) }}</span>
            </span>
          </div>
          <div v-if="buildsStore.currentBuild.image" class="info-item">
            <span class="label">Image</span>
            <span class="value mono">{{ buildsStore.currentBuild.image }}</span>
          </div>
          <div v-if="buildsStore.currentBuild.exitCode !== null" class="info-item">
            <span class="label">Exit Code</span>
            <span class="value" :class="{ 'error-text': buildsStore.currentBuild.exitCode !== 0 }">
              {{ buildsStore.currentBuild.exitCode }}
            </span>
          </div>
        </div>

        <div v-if="buildsStore.currentBuild.errorMessage" class="error-box">
          <span class="error-label">{{ isSuperseded ? 'Reason' : 'Error' }}</span>
          <span v-if="isSuperseded && supersedingBuildId" class="error-message superseded">
            Superseded by
            <RouterLink :to="`/builds/${supersedingBuildId}`" class="superseded-link">
              {{ supersedingBuildId.substring(0, 8) }}
            </RouterLink>
          </span>
          <span v-else class="error-message">{{ buildsStore.currentBuild.errorMessage }}</span>
        </div>

        <!-- Artifacts section -->
        <div v-if="hasArtifacts" class="sidebar-section">
          <h3>Artifacts</h3>
          <div v-if="downloadError" class="download-error">
            {{ downloadError }}
          </div>
          <div class="artifact-list">
            <button
              v-if="hasLogs"
              @click="downloadLogs()"
              :disabled="downloadingLogs"
              class="btn-artifact"
            >
              <span v-if="downloadingLogs">...</span>
              <span v-else>Logs <span class="artifact-size">{{ formatSize(buildsStore.currentBuild.artifacts?.logs?.size || 0) }}</span></span>
            </button>
            <button
              v-for="artifact in stageArtifacts"
              :key="`${artifact.stageName}/${artifact.name}`"
              @click="downloadStageArtifact(artifact.stageName, artifact.name)"
              :disabled="downloadingStageArtifact === `${artifact.stageName}/${artifact.name}`"
              class="btn-artifact"
            >
              <span v-if="downloadingStageArtifact === `${artifact.stageName}/${artifact.name}`">...</span>
              <span v-else>{{ artifact.name }} <span class="artifact-size">{{ formatSize(artifact.size) }}</span></span>
            </button>
          </div>
        </div>

        <!--
          Requires: hard filter. A build only runs on a worker whose
          `labels` contain every key/value shown here. Styled with a
          warning tone so operators can spot strict constraints at a
          glance.
        -->
        <details
          v-if="hasRequires"
          class="sidebar-section labels-details requires-details"
          open
        >
          <summary>Requires ({{ Object.keys(buildRequires).length }})</summary>
          <div class="labels-list">
            <div v-for="(value, key) in buildRequires" :key="key" class="label-item">
              <span class="label-key">{{ key }}</span>
              <span class="label-value">{{ value }}</span>
            </div>
          </div>
        </details>

        <!--
          Prefers: soft filter. Drives ranking (closest match wins) but
          never gates eligibility, so it stays collapsed by default.
        -->
        <details
          v-if="hasPrefers"
          class="sidebar-section labels-details"
        >
          <summary>Prefers ({{ Object.keys(buildPrefers).length }})</summary>
          <div class="labels-list">
            <div v-for="(value, key) in buildPrefers" :key="key" class="label-item">
              <span class="label-key">{{ key }}</span>
              <span class="label-value">{{ value }}</span>
            </div>
          </div>
        </details>
        </div>
      </aside>

      <!-- Main content: Logs -->
      <main class="build-main">
        <DispatchBanner :build="buildsStore.currentBuild" />

        <div v-if="logError" class="ws-error">
          {{ useHistorical ? 'Error loading logs' : 'WebSocket error' }}: {{ logError }}
        </div>
        <LogViewer
          :logs="logs"
          :connected="connected"
          :streaming-state="streamingState"
          :build-status="buildsStore.currentBuild?.status"
          @connect="connect"
          @disconnect="disconnect"
          @clear="clearLogs"
        />
      </main>
    </div>
  </div>
</template>

<style scoped>
.build-detail {
  height: calc(100dvh - 100px); /* viewport minus header (60px) and main padding (40px) */
  display: flex;
  flex-direction: column;
}

/* Top bar */
.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-shrink: 0;
}

.back-btn {
  background: none;
  border: none;
  color: var(--color-accent);
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0;
}

.back-btn:hover {
  text-decoration: underline;
}

.build-actions {
  display: flex;
  gap: 8px;
}

.btn-repeat,
.btn-cancel {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
}

.btn-repeat {
  background: var(--color-btn-primary-bg);
  color: var(--color-btn-primary-text);
}

.btn-repeat:hover {
  background: var(--color-btn-primary-hover);
}

.btn-cancel {
  background: var(--color-btn-danger-bg);
  color: var(--color-btn-danger-text);
}

.btn-cancel:hover {
  background: var(--color-btn-danger-hover);
}

.loading {
  text-align: center;
  padding: 40px;
  color: var(--color-text-secondary);
}

.error {
  color: var(--color-error-text);
}

/* Main layout - sidebar + content */
.build-layout {
  display: flex;
  gap: 20px;
  flex: 1;
  min-height: 0;
}

/* Sidebar */
.build-sidebar {
  width: 280px;
  flex-shrink: 0;
  background: var(--color-card-bg);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid var(--color-card-border);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.build-header {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.build-header h1 {
  font-size: 1.1rem;
  margin: 0;
  color: var(--color-text-primary);
}

.sidebar-toggle {
  display: none;
  margin-left: auto;
  font-size: 1rem;
  color: var(--color-text-muted);
  transition: transform 0.2s;
}

.sidebar-toggle.expanded {
  transform: rotate(180deg);
}

.sidebar-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Info list */
.info-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-item .label {
  font-size: 0.7rem;
  font-weight: 500;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.info-item .value {
  font-size: 0.85rem;
  color: var(--color-text-primary);
  word-break: break-word;
}

.info-item .value.link {
  color: var(--color-accent);
  text-decoration: none;
}

.info-item .value.link:hover {
  text-decoration: underline;
}

.info-item .value.mono {
  font-family: monospace;
}

.info-item .value.error-text {
  color: var(--color-error-text);
}

.time-range {
  display: block;
  font-size: 0.75rem;
  color: var(--color-text-muted);
  margin-top: 2px;
}

/* Error box */
.error-box {
  background: var(--color-error-bg);
  border-radius: 6px;
  padding: 10px;
}

.error-label {
  display: block;
  font-size: 0.7rem;
  font-weight: 500;
  color: var(--color-error-text);
  text-transform: uppercase;
  margin-bottom: 4px;
}

.error-message {
  font-size: 0.85rem;
  color: var(--color-error-text);
  word-break: break-word;
}

.error-message.superseded {
  color: var(--color-text-tertiary);
}

.superseded-link {
  color: var(--color-accent);
  text-decoration: none;
  font-family: monospace;
}

.superseded-link:hover {
  text-decoration: underline;
}

/* Sidebar sections */
.sidebar-section {
  padding-top: 12px;
  border-top: 1px solid var(--color-border-primary);
}

.sidebar-section h3 {
  font-size: 0.8rem;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: var(--color-text-secondary);
}

/* Artifacts */
.artifact-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.btn-artifact {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  background: var(--color-bg-tertiary);
  border: 1px solid var(--color-border-primary);
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  color: var(--color-text-primary);
  transition: background 0.15s;
}

.btn-artifact:hover:not(:disabled) {
  background: var(--color-btn-secondary-hover);
}

.btn-artifact:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.artifact-size {
  color: var(--color-text-muted);
  font-size: 0.75rem;
}

.download-error {
  background: var(--color-error-bg);
  color: var(--color-error-text);
  padding: 6px 8px;
  border-radius: 4px;
  margin-bottom: 8px;
  font-size: 0.8rem;
}

/* Labels (collapsible) */
.labels-details {
  cursor: pointer;
}

.labels-details summary {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--color-text-secondary);
  padding: 4px 0;
  list-style: none;
}

.labels-details summary::-webkit-details-marker {
  display: none;
}

.labels-details summary::before {
  content: '▸ ';
}

.labels-details[open] summary::before {
  content: '▾ ';
}

.labels-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}

.label-item {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.label-key {
  font-size: 0.65rem;
  color: var(--color-text-muted);
  font-family: monospace;
}

.label-value {
  font-size: 0.8rem;
  color: var(--color-text-primary);
  word-break: break-all;
}

/**
 * Requires is a hard filter; hint at that with a subtle warning tint on
 * the summary row so operators distinguish it from the softer "prefers"
 * section without reading the label text.
 */
.requires-details summary {
  color: var(--color-warning-text);
}

.label-pill {
  display: inline-block;
  font-family: monospace;
  font-size: 0.75rem;
  padding: 2px 6px;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  word-break: break-all;
}

/* Trigger badge */
.trigger-badge {
  display: inline-block;
  font-size: 0.7rem;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 500;
}

.trigger-badge.webhook {
  background: var(--color-badge-default-bg);
  color: var(--color-badge-default-text);
}

.trigger-badge.ui,
.trigger-badge.api,
.trigger-badge.cli {
  background: var(--color-success-bg);
  color: var(--color-success-text);
}

.trigger-badge.schedule {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--color-info-bg);
  color: var(--color-info-text);
}

.trigger-badge-icon {
  flex-shrink: 0;
}

/* Main content area (logs) */
.build-main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.build-main :deep(.log-viewer) {
  flex: 1;
  min-height: 0;
  height: auto;
}

.ws-error {
  background: var(--color-error-bg);
  color: var(--color-error-text);
  padding: 10px 14px;
  border-radius: 6px;
  margin-bottom: 12px;
  font-size: 0.85rem;
}

/* Touch-friendly buttons on tablet/phone */
@media (max-width: 768px) {
  .back-btn {
    min-height: 44px;
    display: flex;
    align-items: center;
  }

  .btn-repeat,
  .btn-cancel {
    min-height: 44px;
    padding: 10px 16px;
  }
}

/* Responsive: stack on smaller screens */
@media (max-width: 900px) {
  .build-layout {
    flex-direction: column;
  }

  .build-sidebar {
    width: 100%;
    max-height: none;
    overflow-y: visible;
  }

  .build-header {
    cursor: pointer;
    min-height: 44px;
  }

  .build-header:active {
    opacity: 0.7;
  }

  .sidebar-toggle {
    display: inline;
  }

  .info-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .build-main :deep(.log-viewer) {
    height: calc(100dvh - 180px); /* header 60px + padding 24px + top-bar 48px + sidebar-summary 48px */
    min-height: 350px;
  }
}

@media (max-width: 480px) {
  .build-detail {
    padding: 0;
  }

  .top-bar {
    padding: 0 var(--mobile-padding, 12px);
  }

  .build-sidebar {
    border-radius: 0;
    margin: 0 calc(-1 * var(--mobile-padding, 12px));
    width: calc(100% + 2 * var(--mobile-padding, 12px));
  }

  .info-list {
    grid-template-columns: 1fr;
  }

  .build-header h1 {
    font-size: 1rem;
  }

  .build-main :deep(.log-viewer) {
    height: calc(100dvh - 156px); /* header 60px + top-bar 48px + sidebar-summary 48px */
    min-height: 300px;
  }
}
</style>
